import tickerPollingService from './tickerPollingService';
import { getAccountTickers, getSelectedStatusTickers, getSystemActivities, getAggregatedTickers } from './api';
import { createLogger } from '../utils/debugLogger';

const DEFAULT_POLLING_INTERVAL = 60000;
const MARKET_INDEX_POLL_INTERVAL = 60000;
const ACCOUNT_TICKER_POLL_INTERVAL = 60000;
const BALANCE_STATUS_POLL_INTERVAL = 60000;
const MARKET_STATUS_CHECK_INTERVAL = 300000; // Check market status every 5 minutes

// Create a shim that exposes the old Socket.IO interface while backing it
// with the new polling-only update channel. This allows the rest of the
// application to continue using the same helpers without establishing any
// real WebSocket connections.
const logger = createLogger(
  'socketService',
  'Polling-based ticker channel shim',
  false,
  'INFO'
);

let connectionStatus = 'disconnected';
let initialized = false;

const statusSubscribers = new Set();
const balanceStatusSubscribers = new Set();
const marketIndexSubscribers = new Set();
const accountTickerSubscribers = new Map();
const accountTickerSnapshots = new Map();
const stopoutNotificationSubscribers = new Set();
let statusMonitor = null;
let marketIndexPollTimer = null;
let accountTickerPollTimer = null;
let balanceStatusPollTimer = null;
let stopoutPollTimer = null;
let isMarketIndexPolling = false;
let isAccountTickerPolling = false;
let isBalanceStatusPolling = false;
let isStopoutPolling = false;
let balanceStatusInitialized = false;
let lastProcessedBalanceActivityId = null;
let lastProcessedStopoutId = null;
let stopoutInitialized = false;
let marketStatusCheckTimer = null;
let isMarketClosed = false;
let lastMarketStatusCheck = null;

const dummySocket = {
  connected: false,
  id: 'polling-channel',
  connect: () => ensureActive(),
  disconnect: () => stopChannel(),
  on: () => {},
  off: () => {}
};

const notifyStatusSubscribers = () => {
  statusSubscribers.forEach((callback) => {
    try {
      callback(connectionStatus);
    } catch (error) {
      logger.error('Error in socket status subscriber', error);
    }
  });
};

const setConnectionStatus = (status) => {
  if (connectionStatus === status) {
    return;
  }

  connectionStatus = status;
  dummySocket.connected = status === 'connected';
  notifyStatusSubscribers();
};

const startStatusMonitor = () => {
  if (statusMonitor) {
    return;
  }

  statusMonitor = setInterval(() => {
    const status = tickerPollingService.getStatus();

    if (!status.isEnabled) {
      setConnectionStatus('disconnected');
      return;
    }

    if (status.isPolling) {
      setConnectionStatus('connected');
    } else {
      setConnectionStatus('connecting');
    }
  }, 5000); // 5 seconds
};

const stopStatusMonitor = () => {
  if (statusMonitor) {
    clearInterval(statusMonitor);
    statusMonitor = null;
  }
};

const startChannel = () => {
  if (!initialized) {
    initialized = true;
  }

  if (connectionStatus === 'connected') {
    startStatusMonitor();
    startMarketStatusMonitoring();
    return;
  }

  logger.info('Starting polling channel for ticker updates');
  setConnectionStatus('connecting');
  tickerPollingService.setEnabled(true);
  tickerPollingService.startPolling(DEFAULT_POLLING_INTERVAL);
  setConnectionStatus('connected');
  startStatusMonitor();
  startMarketStatusMonitoring();
};

const stopChannel = () => {
  if (connectionStatus === 'disconnected') {
    return;
  }

  logger.info('Stopping polling channel for ticker updates');
  tickerPollingService.stopPolling();
  stopMarketIndexPolling();
  stopAccountTickerPolling();
  stopBalanceStatusPolling();
  stopStopoutPolling();
  stopMarketStatusMonitoring();
  setConnectionStatus('disconnected');
  stopStatusMonitor();
};

const ensureActive = () => {
  if (connectionStatus === 'disconnected') {
    startChannel();
  }
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined) {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeDetails = (details) => {
  if (!details) {
    return {};
  }

  if (typeof details === 'object' && !Array.isArray(details)) {
    return details;
  }

  try {
    return JSON.parse(details);
  } catch (error) {
    return {};
  }
};

const extractAccountNames = (accounts) => {
  if (!Array.isArray(accounts)) {
    return undefined;
  }

  return accounts.map((account) => {
    if (typeof account === 'string') {
      return account;
    }

    if (account && typeof account === 'object') {
      return account.name || account.account_name || account.id || account.account_id || JSON.stringify(account);
    }

    return String(account);
  });
};

const transformActivityToBalanceEvent = (activity) => {
  if (!activity) {
    return null;
  }

  const { activity_type: activityType, status, description } = activity;
  const details = normalizeDetails(activity.details);
  const message = description || details?.message || `Balance update status: ${status}`;

  switch (activityType) {
    case 'daily_balance_save': {
      return {
        type: 'daily_balance_save',
        status,
        success_count: normalizeNumber(details?.success_count),
        failed_count: normalizeNumber(details?.failed_count),
        total_accounts: normalizeNumber(details?.total_accounts),
        message
      };
    }
    case 'daily_balance_verification': {
      if (status === 'auto_healed') {
        return {
          type: 'balance_auto_healing_success',
          status: 'success',
          message,
          recovered_count: normalizeNumber(details?.recovery_success_count ?? details?.recovery_count)
        };
      }

      if (status === 'partial_recovery') {
        const remainingFailed = extractAccountNames(details?.remaining_failed_accounts);
        return {
          type: 'balance_partial_recovery',
          status: 'warning',
          message,
          recovered_count: normalizeNumber(details?.recovery_success_count ?? details?.recovery_count),
          remaining_failed: remainingFailed,
          still_missing: normalizeNumber(details?.still_missing ?? details?.missing_count ?? details?.remaining_missing_count)
        };
      }

      if (status === 'recovery_failed_backup_run' || status === 'recovery_failed_monitoring') {
        return {
          type: 'balance_recovery_failed',
          status: status === 'recovery_failed_backup_run' ? 'warning' : 'monitoring',
          message,
          failed_accounts: extractAccountNames(details?.recovery_failed_accounts ?? details?.remaining_failed_accounts)
        };
      }

      if (status === 'error') {
        return {
          type: 'balance_verification_error',
          status: 'error',
          message,
          error: details?.error || message
        };
      }

      return {
        type: 'balance_verification_info',
        status,
        message,
        details
      };
    }
    case 'early_balance_detection': {
      if (status === 'auto_healed') {
        return {
          type: 'early_detection_success',
          status: 'success',
          message,
          recovered_count: normalizeNumber(details?.recovery_count)
        };
      }

      if (status === 'detected_unfixed') {
        return {
          type: 'early_detection_warning',
          status: 'info',
          message,
          missing_count: normalizeNumber(details?.missing_count)
        };
      }

      return {
        type: 'balance_verification_info',
        status,
        message,
        details
      };
    }
    default:
      return null;
  }
};

const notifyMarketIndexSubscribers = (payload) => {
  marketIndexSubscribers.forEach((callback) => {
    try {
      callback(payload);
    } catch (error) {
      logger.error('Error in market index subscriber', error);
    }
  });
};

const checkMarketStatus = async () => {
  try {
    const response = await getAggregatedTickers();
    const marketClosed = response?.data?.market_closed ?? false;
    const previousMarketClosed = isMarketClosed;
    isMarketClosed = marketClosed;
    lastMarketStatusCheck = new Date();

    logger.debug('Market status checked', {
      isMarketClosed: marketClosed,
      changed: previousMarketClosed !== marketClosed,
      time: lastMarketStatusCheck.toISOString()
    });

    // If market just opened, restart all polling
    if (previousMarketClosed && !marketClosed) {
      logger.info('Market opened - restarting all polling timers');
      if (marketIndexSubscribers.size > 0) {
        startMarketIndexPolling();
      }
      if (accountTickerSubscribers.size > 0) {
        startAccountTickerPolling();
      }
      if (balanceStatusSubscribers.size > 0) {
        startBalanceStatusPolling();
      }
      if (stopoutNotificationSubscribers.size > 0) {
        startStopoutPolling();
      }
    }
    // If market just closed, stop all polling
    else if (!previousMarketClosed && marketClosed) {
      logger.info('Market closed - stopping all polling timers');
      stopMarketIndexPolling();
      stopAccountTickerPolling();
      stopBalanceStatusPolling();
      stopStopoutPolling();
    }
  } catch (error) {
    logger.error('Error checking market status', error);
  }
};

const startMarketStatusMonitoring = () => {
  if (marketStatusCheckTimer) {
    return;
  }

  logger.info('Starting market status monitoring');
  checkMarketStatus(); // Check immediately
  marketStatusCheckTimer = setInterval(checkMarketStatus, MARKET_STATUS_CHECK_INTERVAL);
};

const stopMarketStatusMonitoring = () => {
  if (marketStatusCheckTimer) {
    clearInterval(marketStatusCheckTimer);
    marketStatusCheckTimer = null;
    logger.info('Stopped market status monitoring');
  }
};

const pollMarketIndexes = async () => {
  if (isMarketIndexPolling || marketIndexSubscribers.size === 0 || isMarketClosed) {
    return;
  }

  isMarketIndexPolling = true;

  try {
    const response = await getSelectedStatusTickers();
    const indexes = Array.isArray(response?.data) ? response.data : [];

    let latestTime = null;
    indexes.forEach((indexTicker) => {
      if (indexTicker?.last_updated) {
        try {
          const timestamp = new Date(indexTicker.last_updated);
          if (!Number.isNaN(timestamp.getTime())) {
            if (!latestTime || timestamp > latestTime) {
              latestTime = timestamp;
            }
          }
        } catch (error) {
          logger.debug('Unable to parse market index timestamp', {
            symbol: indexTicker?.symbol,
            last_updated: indexTicker?.last_updated,
            error
          });
        }
      }
    });

    const payload = {
      indexes,
      market_data: indexes,
      time: (latestTime || new Date()).toISOString()
    };

    notifyMarketIndexSubscribers(payload);
  } catch (error) {
    logger.error('Error polling market index updates', error);
  } finally {
    isMarketIndexPolling = false;
  }
};

const startMarketIndexPolling = () => {
  if (marketIndexPollTimer || isMarketClosed) {
    return;
  }

  logger.info('Starting market index polling');
  pollMarketIndexes();
  marketIndexPollTimer = setInterval(pollMarketIndexes, MARKET_INDEX_POLL_INTERVAL);
};

const stopMarketIndexPolling = () => {
  if (marketIndexPollTimer) {
    logger.info('Stopping market index polling');
    clearInterval(marketIndexPollTimer);
    marketIndexPollTimer = null;
  }
};

const notifyAccountTickerSubscribers = (accountId, payload) => {
  const accountKey = String(accountId);
  const callbacks = new Set();

  const specificSubscribers = accountTickerSubscribers.get(accountKey);
  if (specificSubscribers) {
    specificSubscribers.forEach((callback) => callbacks.add(callback));
  }

  const wildcardSubscribers = accountTickerSubscribers.get('__all__');
  if (wildcardSubscribers) {
    wildcardSubscribers.forEach((callback) => callbacks.add(callback));
  }

  callbacks.forEach((callback) => {
    try {
      callback(payload);
    } catch (error) {
      logger.error('Error in account ticker subscriber', error);
    }
  });
};

const pollAccountTickerUpdates = async () => {
  if (isAccountTickerPolling || accountTickerSubscribers.size === 0 || isMarketClosed) {
    return;
  }

  const accountKeys = Array.from(accountTickerSubscribers.keys()).filter((key) => key !== '__all__');
  if (accountKeys.length === 0) {
    return;
  }

  isAccountTickerPolling = true;

  try {
    await Promise.all(accountKeys.map(async (accountKey) => {
      const numericAccountId = Number(accountKey);

      if (!Number.isFinite(numericAccountId)) {
        return;
      }

      try {
        const response = await getAccountTickers(numericAccountId);
        const tickers = Array.isArray(response?.data) ? response.data : [];
        const previousSnapshot = accountTickerSnapshots.get(accountKey);
        const isFirstPollForAccount = !previousSnapshot;
        const nextSnapshot = new Map();

        tickers.forEach((ticker) => {
          if (!ticker || typeof ticker !== 'object') {
            return;
          }

          const tickerId = ticker.ticker_id ?? ticker.id;
          if (!tickerId && tickerId !== 0) {
            return;
          }

          const entry = {
            shield_risk_percentage: normalizeNumber(ticker.shield_risk_percentage),
            shield_risk_amount: normalizeNumber(ticker.shield_risk_amount),
            symbol: ticker.symbol
          };

          nextSnapshot.set(String(tickerId), entry);

          if (!isFirstPollForAccount) {
            const previousEntry = previousSnapshot?.get(String(tickerId));
            const hasRiskChange =
              !previousEntry ||
              previousEntry.shield_risk_percentage !== entry.shield_risk_percentage ||
              previousEntry.shield_risk_amount !== entry.shield_risk_amount;

            if (hasRiskChange) {
              notifyAccountTickerSubscribers(numericAccountId, {
                type: 'risk_update',
                account_id: numericAccountId,
                ticker_id: tickerId,
                symbol: ticker.symbol,
                shield_risk_percentage: entry.shield_risk_percentage,
                shield_risk_amount: entry.shield_risk_amount
              });
            }
          }
        });

        accountTickerSnapshots.set(accountKey, nextSnapshot);
      } catch (error) {
        logger.error('Error polling account ticker updates', {
          accountId: numericAccountId,
          error
        });
      }
    }));
  } finally {
    isAccountTickerPolling = false;
  }
};

const startAccountTickerPolling = () => {
  if (accountTickerPollTimer || isMarketClosed) {
    return;
  }

  logger.info('Starting account ticker polling');
  pollAccountTickerUpdates();
  accountTickerPollTimer = setInterval(pollAccountTickerUpdates, ACCOUNT_TICKER_POLL_INTERVAL);
};

const stopAccountTickerPolling = () => {
  if (accountTickerPollTimer) {
    logger.info('Stopping account ticker polling');
    clearInterval(accountTickerPollTimer);
    accountTickerPollTimer = null;
  }

  accountTickerSnapshots.clear();
};

const pollBalanceUpdateStatus = async () => {
  if (isBalanceStatusPolling || balanceStatusSubscribers.size === 0 || isMarketClosed) {
    return;
  }

  isBalanceStatusPolling = true;

  try {
    const response = await getSystemActivities({ days: 2, limit: 50 });
    const activities = Array.isArray(response?.data?.activities) ? response.data.activities : [];

    if (activities.length === 0) {
      if (!balanceStatusInitialized) {
        balanceStatusInitialized = true;
      }
      return;
    }

    const sortedActivities = activities
      .filter((activity) => activity && typeof activity === 'object')
      .sort((a, b) => (a.activity_id || 0) - (b.activity_id || 0));

    if (sortedActivities.length === 0) {
      if (!balanceStatusInitialized) {
        balanceStatusInitialized = true;
      }
      return;
    }

    const mostRecentActivityId = sortedActivities[sortedActivities.length - 1].activity_id;

    if (!balanceStatusInitialized) {
      balanceStatusInitialized = true;
      lastProcessedBalanceActivityId = mostRecentActivityId ?? lastProcessedBalanceActivityId;
      return;
    }

    let maxProcessedId = lastProcessedBalanceActivityId ?? 0;

    sortedActivities.forEach((activity) => {
      const activityId = activity.activity_id ?? 0;
      if (activityId <= (lastProcessedBalanceActivityId ?? 0)) {
        return;
      }

      const event = transformActivityToBalanceEvent(activity);
      if (event) {
        emitBalanceUpdateStatus(event);
      }

      if (activityId > maxProcessedId) {
        maxProcessedId = activityId;
      }
    });

    lastProcessedBalanceActivityId = maxProcessedId;
  } catch (error) {
    logger.error('Error polling balance update status', error);
  } finally {
    isBalanceStatusPolling = false;
  }
};

const startBalanceStatusPolling = () => {
  if (balanceStatusPollTimer || isMarketClosed) {
    return;
  }

  logger.info('Starting balance status polling');
  pollBalanceUpdateStatus();
  balanceStatusPollTimer = setInterval(pollBalanceUpdateStatus, BALANCE_STATUS_POLL_INTERVAL);
};

const stopBalanceStatusPolling = () => {
  if (balanceStatusPollTimer) {
    logger.info('Stopping balance status polling');
    clearInterval(balanceStatusPollTimer);
    balanceStatusPollTimer = null;
  }

  balanceStatusInitialized = false;
  lastProcessedBalanceActivityId = null;
};

const STOPOUT_POLL_INTERVAL = 15000; // Poll for stopouts every 15 seconds

const notifyStopoutSubscribers = (payload) => {
  stopoutNotificationSubscribers.forEach((callback) => {
    try {
      callback(payload);
    } catch (error) {
      logger.error('Error in stopout notification subscriber', error);
    }
  });
};

const pollStopoutNotifications = async () => {
  if (isStopoutPolling || stopoutNotificationSubscribers.size === 0) {
    return;
  }

  isStopoutPolling = true;

  try {
    const { getStopouts } = await import('./api');
    const response = await getStopouts();
    const stopouts = Array.isArray(response?.data) ? response.data : [];

    if (stopouts.length === 0) {
      if (!stopoutInitialized) {
        stopoutInitialized = true;
      }
      return;
    }

    // Sort by id to process in order
    const sortedStopouts = stopouts
      .filter((stopout) => stopout && typeof stopout === 'object' && stopout.id)
      .sort((a, b) => (a.id || 0) - (b.id || 0));

    if (sortedStopouts.length === 0) {
      if (!stopoutInitialized) {
        stopoutInitialized = true;
      }
      return;
    }

    const mostRecentStopoutId = sortedStopouts[sortedStopouts.length - 1].id;

    // On first poll, just record the latest ID without notifying
    if (!stopoutInitialized) {
      stopoutInitialized = true;
      lastProcessedStopoutId = mostRecentStopoutId;
      return;
    }

    let maxProcessedId = lastProcessedStopoutId || 0;

    // Notify about new stopouts
    sortedStopouts.forEach((stopout) => {
      const stopoutId = stopout.id || 0;
      if (stopoutId <= (lastProcessedStopoutId || 0)) {
        return;
      }

      // Emit notification for new stopout
      notifyStopoutSubscribers({
        type: 'stopout_triggered',
        ticker: stopout.ticker,
        account_name: stopout.account_name,
        trigger_price: stopout.trigger_price,
        shield_price: stopout.shield_price,
        shield_name: stopout.shield_name,
        triggered_at: stopout.triggered_at || stopout.created_at,
        gain: stopout.gain,
        gain_pct: stopout.gain_pct,
        purchase_price: stopout.purchase_price
      });

      if (stopoutId > maxProcessedId) {
        maxProcessedId = stopoutId;
      }
    });

    lastProcessedStopoutId = maxProcessedId;
  } catch (error) {
    logger.error('Error polling stopout notifications', error);
  } finally {
    isStopoutPolling = false;
  }
};

const startStopoutPolling = () => {
  if (stopoutPollTimer) {
    return;
  }

  logger.info('Starting stopout notification polling');
  pollStopoutNotifications();
  stopoutPollTimer = setInterval(pollStopoutNotifications, STOPOUT_POLL_INTERVAL);
};

const stopStopoutPolling = () => {
  if (stopoutPollTimer) {
    logger.info('Stopping stopout notification polling');
    clearInterval(stopoutPollTimer);
    stopoutPollTimer = null;
  }

  stopoutInitialized = false;
  lastProcessedStopoutId = null;
};

export const subscribeToStopoutNotifications = (callback) => {
  if (typeof callback !== 'function') {
    return () => {};
  }

  stopoutNotificationSubscribers.add(callback);
  startStopoutPolling();

  return () => {
    stopoutNotificationSubscribers.delete(callback);
    if (stopoutNotificationSubscribers.size === 0) {
      stopStopoutPolling();
    }
  };
};

export const initSocket = () => {
  logger.info('Initializing polling-based ticker channel');
  startChannel();
  return dummySocket;
};

export const getSocket = () => dummySocket;

export const subscribeToSocketStatus = (callback) => {
  if (typeof callback !== 'function') {
    return () => {};
  }

  statusSubscribers.add(callback);
  callback(connectionStatus);

  return () => {
    statusSubscribers.delete(callback);
  };
};

export const subscribeToTickerUpdates = (maybeCallback, maybeOptions) => {
  let callback = maybeCallback;
  let options = maybeOptions;

  if (typeof maybeCallback === 'object' && maybeCallback !== null && typeof maybeCallback !== 'function') {
    options = maybeCallback;
    callback = maybeCallback.callback || maybeCallback.onUpdate;
  }

  if (typeof callback !== 'function') {
    return () => {};
  }

  const normalizedOptions = (typeof options === 'object' && options !== null) ? options : {};
  const { autoReactivate = true } = normalizedOptions;

  if (autoReactivate) {
    ensureActive();
  }

  return tickerPollingService.subscribe((data) => {
    try {
      callback({
        ...data,
        source: 'polling'
      });
    } catch (error) {
      logger.error('Error in ticker update subscriber', error);
    }
  });
};

export const subscribeToAccountTickerUpdates = (options, maybeCallback) => {
  let accountId = null;
  let callback = null;

  if (typeof options === 'function') {
    callback = options;
  } else if (typeof options === 'object' && options !== null) {
    if (typeof maybeCallback === 'function') {
      callback = maybeCallback;
    } else if (typeof options.callback === 'function') {
      callback = options.callback;
    }
    if (options.accountId !== undefined) {
      accountId = options.accountId;
    } else if (options.account_id !== undefined) {
      accountId = options.account_id;
    }
  } else {
    accountId = options;
    callback = maybeCallback;
  }

  if (typeof callback !== 'function') {
    return () => {};
  }

  ensureActive();

  const accountKey = accountId !== null && accountId !== undefined ? String(accountId) : '__all__';
  if (!accountTickerSubscribers.has(accountKey)) {
    accountTickerSubscribers.set(accountKey, new Set());
  }

  const subscribers = accountTickerSubscribers.get(accountKey);
  subscribers.add(callback);

  if (!isMarketClosed) {
    startAccountTickerPolling();
  }

  return () => {
    const currentSubscribers = accountTickerSubscribers.get(accountKey);
    if (currentSubscribers) {
      currentSubscribers.delete(callback);
      if (currentSubscribers.size === 0) {
        accountTickerSubscribers.delete(accountKey);
        if (accountKey !== '__all__') {
          accountTickerSnapshots.delete(accountKey);
        }
      }
    }

    if (accountTickerSubscribers.size === 0) {
      stopAccountTickerPolling();
    }
  };
};

export const subscribeToMarketIndexUpdates = (callback) => {
  if (typeof callback !== 'function') {
    return () => {};
  }

  ensureActive();

  marketIndexSubscribers.add(callback);
  if (!isMarketClosed) {
    startMarketIndexPolling();
  }

  return () => {
    marketIndexSubscribers.delete(callback);
    if (marketIndexSubscribers.size === 0) {
      stopMarketIndexPolling();
    }
  };
};

export const subscribeToBalanceUpdateStatus = (callback) => {
  if (typeof callback !== 'function') {
    return () => {};
  }

  balanceStatusSubscribers.add(callback);

  if (!isMarketClosed) {
    startBalanceStatusPolling();
  }

  return () => {
    balanceStatusSubscribers.delete(callback);
    if (balanceStatusSubscribers.size === 0) {
      stopBalanceStatusPolling();
    }
  };
};

export const emitBalanceUpdateStatus = (payload) => {
  balanceStatusSubscribers.forEach((callback) => {
    try {
      callback(payload);
    } catch (error) {
      logger.error('Error in balance update status subscriber', error);
    }
  });
};

export const getSocketStatus = () => ({
  initialized,
  connected: connectionStatus === 'connected',
  connectionStatus,
  id: dummySocket.id,
  transport: 'polling',
  url: 'polling://local',
  readyState: connectionStatus,
  error: null
});

export const isSocketConnected = () => connectionStatus === 'connected';

export const disconnectSocket = () => {
  stopChannel();
};

export const reconnectSocket = () => {
  startChannel();
};

const socketService = {
  initSocket,
  getSocket,
  subscribeToSocketStatus,
  subscribeToTickerUpdates,
  subscribeToAccountTickerUpdates,
  subscribeToMarketIndexUpdates,
  subscribeToBalanceUpdateStatus,
  subscribeToStopoutNotifications,
  emitBalanceUpdateStatus,
  getSocketStatus,
  isSocketConnected,
  disconnectSocket,
  reconnectSocket
};

export default socketService;
